/**
 * API service for company portal
 *
 * Uses JWT authentication from auth store.
 * API base URL comes from VITE_API_URL env variable.
 */
import { useAuthStore } from '@/stores/auth'
import { startTrace, traceHeaders } from '@/services/telemetry'
import { SpanStatusCode } from '@lark-automadoc/shared-types/otel'
import { collapseSpanPath } from '@lark-automadoc/shared-types/otel/browser'
import {
  createEndpoints,
  requiresReauth,
  type GetTenantInfoResponse,
  type StartTrialResponse,
  type BillingPortalResponse,
  type CheckoutTokenResponse,
  type BillingExtraTemplatesResponse,
  type BillingSummaryResponse,
  type BillingSeatsRequest,
  type BillingSeatsResponse,
  type GetTenantSeatsResponse,
  type UpdateTenantSeatRequest,
  type RefreshNamesResponse,
  type GetTenantTemplatesResponse,
  type DeleteTenantTemplateResponse,
  type GetTenantGenerationsResponse,
  type GetTenantTemplateRowsResponse,
  type GetPeopleResponse,
  type GetLarkBasesResponse,
  type SupportRequest,
  type SupportRequestResponse,
  type ContactPreferencesResponse,
  type UpdateContactPreferencesRequest,
  type ApiErrorResponse,
  type GetApprovalTemplatesResponse,
  type GetApprovalTemplateResponse,
  type ApprovalTemplateResponse,
  type CreateApprovalTemplateRequest,
  type UpdateApprovalTemplateRequest,
  type GetApprovalRequestsResponse,
  type StartApprovalRunResponse,
  type GetApprovalRunsResponse,
  type GetApprovalChatsResponse,
  type GetSignatureRequestResponse,
  type ListSignatureRequestsResponse,
  type RemindSignerResponse,
  type GenerateRequest,
  type GenerateResponse,
  type GetTemplateDesignResponse,
  type PublishTemplateDesignRequest,
  type PublishTemplateDesignResponse,
  type SaveTemplateDesignRequest,
  type SaveTemplateDesignResponse,
  type DesignSource,
  type PreviewDesignRequest,
  type PutDesignSampleRequest,
  type GetApproverSignaturesResponse,
  type ApproverSignatureResponse,
  type CreateTenantApiKeyResponse,
  type TenantApiKeySummary,
  type MemberRole,
  type ListInvitationsResponse,
  type CreateInvitationResponse,
  type JoinPolicy,
  type JoinPolicyResponse,
  type JoinRequestDecisionResponse,
  type SeatTier,
  type OAuthProvider,
  type ProviderConnectionStatus,
  type DataverseEnvironmentsResponse,
  type AuthoringTemplateSummary,
  type AuthoringTemplateDetail,
  type AutoGenerateTemplateRequest,
  type ClientSourceView,
  type DataverseTablesResponse,
  type SharePointListsResponse,
  type SharePointSitesResponse,
  type SourceColumnsResponse,
  type UpdateTemplateRequest,
  type SourceTemplate,
  type SourceTemplateFileResponse,
  esignPath,
  type AddGeneratedDocumentBody,
  type ApplyTagsResponse,
  type CreateEnvelopeBody,
  type DocumentPagesResponse,
  type DocumentResponse,
  type EnvelopeEventsResponse,
  type EnvelopeFieldInput,
  type EnvelopeRecipientInput,
  type EnvelopeResponse,
  type EsignDownload,
  type EsignDownloadFile,
  type EsignSavedSignatureKind,
  type FieldsResponse,
  type LayoutResponse,
  type ListEnvelopesQuery,
  type ListEnvelopesResponse,
  type ListLayoutsResponse,
  type RecipientsResponse,
  type RemindRecipientResponse,
  type ResendLinkResponse,
  type SavedSignaturesResponse,
  type SelfSignPayload,
  type SendEnvelopeResponse,
  type UpdateEnvelopeBody,
  type UpdateLayoutBody,
  type PortalGenerateInfo,
  type PortalGenerateRequest,
  type PortalGenerateResponse,
  type WSJobStatusResponse,
} from '@lark-automadoc/shared-types'

// Create endpoints with API base URL from env
const API_BASE = import.meta.env.VITE_API_URL || ''
export const API_ENDPOINTS = createEndpoints(API_BASE)

/**
 * One request, with everything every call needs: the JWT, a span, the
 * expired-session handling and an error that carries the server's `code`.
 *
 * `read` turns the successful response into the caller's value, so a download
 * (a blob plus a header) goes through here like any JSON call rather than
 * through a second fetch that would miss the tracing and the 401 handling.
 */
async function apiRequest<T>(
  url: string,
  options: RequestInit,
  read: (response: Response) => Promise<T>,
): Promise<T> {
  const authStore = useAuthStore()
  if (!authStore.token) {
    throw new Error('Not authenticated')
  }

  // One span per API call — instrumenting the wrapper covers every request,
  // including ones added later.
  const method = options.method ?? 'GET'
  const span = startTrace(`${method} ${spanPath(url)}`, {
    'http.request.method': method,
    'url.path': spanPath(url),
  })

  const headers = {
    Authorization: `Bearer ${authStore.token}`,
    // A FormData body must set its own Content-Type: the multipart boundary is
    // part of it, and only the browser knows the boundary.
    ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
    ...options.headers,
    ...traceHeaders(span, url),
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers,
    })

    span?.setAttribute('http.response.status_code', response.status)

    if (!response.ok) {
      const errorData: ApiErrorResponse = await response.json().catch(() => ({ error: `API error: ${response.status}` }))

      // Handle 401 - clear session so user can retry
      if (response.status === 401 && requiresReauth(errorData.code)) {
        // Deliberately drops the server's English `error`: App.vue translates
        // what the store puts here, so a raw sentence would render
        // untranslated in all seven locales.
        authStore.clearSession(errorData.code)
      }

      // `code` rides along so a view can translate the failure instead of
      // showing the server's English sentence; `body` so one that carries
      // more than a code (a quota refusal says what is on sale) can be read.
      throw Object.assign(new Error(errorData.error || errorData.message || `API error: ${response.status}`), {
        code: errorData.code,
        status: response.status,
        // The rest of the body, for errors that carry more than a code: `details`
        // is what the designer reads, `body` what the billing screens read.
        details: errorData,
        body: errorData,
      })
    }

    span?.setStatus(SpanStatusCode.OK)
    return await read(response)
  } catch (error) {
    span?.recordException(error)
    throw error
  } finally {
    span?.end()
  }
}

/** A JSON API call. */
function apiFetch<T>(url: string, options: RequestInit = {}): Promise<T> {
  return apiRequest<T>(url, options, response => response.json())
}

/**
 * Ids collapsed so span names stay low-cardinality — a span per template id
 * would make the name useless for grouping. Approval templates are the one
 * collection only this portal addresses by id; the rest are in the shared
 * COMMON_ID_SEGMENTS.
 */
const spanPath = (url: string): string =>
  collapseSpanPath(url, { envelopes: ':envelopeId', documents: ':documentId', recipients: ':recipientId', layouts: ':layoutId', approvals: ':templateId', 'approver-signatures': ':openId', signatures: ':requestId', signers: ':signerId', 'api-keys': ':keyId', members: ':userId', invitations: ':invitationId' })

/** A list route's query string, leaving out what is empty. */
function listParams(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value))
  }
  return search.toString()
}

// =============================================================================
// Tenant API
// =============================================================================

export const tenantApi = {
  async getTenantInfo(): Promise<GetTenantInfoResponse> {
    return apiFetch<GetTenantInfoResponse>(API_ENDPOINTS.getTenantInfo())
  },

  async refreshNames(): Promise<RefreshNamesResponse> {
    return apiFetch<RefreshNamesResponse>(API_ENDPOINTS.refreshNames(), { method: 'POST' })
  },

  async startTrial(): Promise<StartTrialResponse> {
    return apiFetch(API_ENDPOINTS.startTrial(), { method: 'POST' })
  },

  /**
   * Mint a short-lived signed token identifying this tenant for a Paddle
   * checkout. Passed as custom_data — the webhook trusts nothing else, since
   * the checkout overlay runs in the browser.
   */
  async createCheckoutToken(): Promise<CheckoutTokenResponse> {
    return apiFetch<CheckoutTokenResponse>(API_ENDPOINTS.checkoutToken(), { method: 'POST' })
  },

  /** Create a Paddle customer-portal session and return its URL. */
  async createBillingPortalSession(): Promise<BillingPortalResponse> {
    return apiFetch<BillingPortalResponse>(API_ENDPOINTS.billingPortal(), { method: 'POST' })
  },

  /**
   * Add to the per-unit Extra Templates add-on. Raises the quantity on an
   * existing subscription, or returns needsCheckout when there's none yet.
   */
  async addExtraTemplates(addQuantity: number): Promise<BillingExtraTemplatesResponse> {
    return apiFetch<BillingExtraTemplatesResponse>(API_ENDPOINTS.billingExtraTemplates(), {
      method: 'POST',
      body: JSON.stringify({ addQuantity }),
    })
  },

  /** Seats, packs, the automation allowance and the products this environment sells. */
  async getBillingSummary(): Promise<BillingSummaryResponse> {
    return apiFetch<BillingSummaryResponse>(API_ENDPOINTS.billingSummary())
  },

  /**
   * Set a seat subscription's quantity — the new *total*, never a delta, so a
   * double click cannot buy twice. Answers needsCheckout when the tenant has no
   * subscription for that tier and period yet.
   */
  async updateSeats(body: BillingSeatsRequest): Promise<BillingSeatsResponse> {
    return apiFetch<BillingSeatsResponse>(API_ENDPOINTS.billingSeats(), {
      method: 'POST',
      body: JSON.stringify(body),
    })
  },

  async getTenantSeats(): Promise<GetTenantSeatsResponse> {
    return apiFetch<GetTenantSeatsResponse>(API_ENDPOINTS.tenantSeats())
  },

  /** Assign a person a seat of a tier, move them to the other tier, or unassign them (tier null). */
  async updateTenantSeat(body: UpdateTenantSeatRequest): Promise<GetTenantSeatsResponse> {
    return apiFetch<GetTenantSeatsResponse>(API_ENDPOINTS.tenantSeats(), {
      method: 'PUT',
      body: JSON.stringify(body),
    })
  },

  async getTenantTemplates(): Promise<GetTenantTemplatesResponse> {
    return apiFetch<GetTenantTemplatesResponse>(API_ENDPOINTS.getTenantTemplates())
  },

  async deleteTenantTemplate(templateId: string): Promise<DeleteTenantTemplateResponse> {
    return apiFetch(API_ENDPOINTS.deleteTenantTemplate(templateId), { method: 'DELETE' })
  },

  /**
   * One page of the person's documents. `q` searches file and template names; `status`,
   * `source` and `sort` are the Documents table's filters and order (see the route).
   */
  async getTenantGenerations(
    page: number = 1,
    limit: number = 20,
    options: { q?: string; status?: string; source?: string; sort?: string } = {},
  ): Promise<GetTenantGenerationsResponse> {
    return apiFetch<GetTenantGenerationsResponse>(`${API_ENDPOINTS.getTenantGenerations()}?${listParams({ page, limit, ...options })}`)
  },

  /** Every template of the account, whatever feeds it. */
  async getTemplateRows(): Promise<GetTenantTemplateRowsResponse> {
    return apiFetch<GetTenantTemplateRowsResponse>(API_ENDPOINTS.tenantTemplateRows())
  },

  /** Everyone in the account, with role, status and last activity. */
  async getPeople(): Promise<GetPeopleResponse> {
    return apiFetch<GetPeopleResponse>(API_ENDPOINTS.tenantPeople())
  },

  async getLarkBases(): Promise<GetLarkBasesResponse> {
    return apiFetch<GetLarkBasesResponse>(API_ENDPOINTS.getLarkBases())
  },
}

// =============================================================================
// People: members, join requests, invitations, connections
// =============================================================================

/**
 * People in the account (app_admin), for every platform: roles, who may join from
 * the account's organisation and the requests waiting, and invitations. Every write
 * re-derives the admin answer (requireFreshAdmin); FRESH_TOKEN_REQUIRED means the
 * person must sign in again, which People says.
 */
export const accountApi = {
  setMemberRole(userId: string, role: MemberRole): Promise<{ success: boolean }> {
    return apiFetch(API_ENDPOINTS.tenantMember(userId), { method: 'PUT', body: JSON.stringify({ role }) })
  },
  removeMember(userId: string): Promise<{ success: boolean; revokedSessions?: number }> {
    return apiFetch(API_ENDPOINTS.tenantMember(userId), { method: 'DELETE' })
  },
  listInvitations(): Promise<ListInvitationsResponse> {
    return apiFetch(API_ENDPOINTS.tenantInvitations())
  },
  invite(email: string, role: MemberRole, seat: SeatTier | null, locale: string): Promise<CreateInvitationResponse> {
    return apiFetch(API_ENDPOINTS.tenantInvitations(), { method: 'POST', body: JSON.stringify({ email, role, seat, locale }) })
  },
  /** A new link and the email again; the old link stops working. */
  resendInvitation(id: string, locale: string): Promise<CreateInvitationResponse> {
    return apiFetch(API_ENDPOINTS.tenantInvitationResend(id), { method: 'POST', body: JSON.stringify({ locale }) })
  },
  joinPolicy(): Promise<JoinPolicyResponse> {
    return apiFetch(API_ENDPOINTS.tenantJoinPolicy())
  },
  setJoinPolicy(policy: JoinPolicy): Promise<JoinPolicyResponse> {
    return apiFetch(API_ENDPOINTS.tenantJoinPolicy(), { method: 'PUT', body: JSON.stringify({ policy }) })
  },
  approveRequest(userId: string, role: MemberRole): Promise<JoinRequestDecisionResponse> {
    return apiFetch(API_ENDPOINTS.tenantJoinRequestApprove(userId), { method: 'POST', body: JSON.stringify({ role }) })
  },
  declineRequest(userId: string): Promise<JoinRequestDecisionResponse> {
    return apiFetch(API_ENDPOINTS.tenantJoinRequestDecline(userId), { method: 'POST' })
  },
  withdrawInvitation(id: string): Promise<{ success: boolean }> {
    return apiFetch(API_ENDPOINTS.tenantInvitation(id), { method: 'DELETE' })
  },
}

/**
 * The signed-in person's own Microsoft and Google data connections. Connecting
 * is a redirect round trip (stores/auth.ts startConnect); these read and undo.
 */
export const connectionsApi = {
  status(provider: OAuthProvider): Promise<ProviderConnectionStatus> {
    return apiFetch(API_ENDPOINTS.tenantConnection(provider))
  },
  /** Microsoft answers `{ disconnected, manageUrl }`, Google `{ success, revokedAtGoogle }`; the page reads neither. */
  disconnect(provider: OAuthProvider): Promise<unknown> {
    return apiFetch(API_ENDPOINTS.tenantConnection(provider), { method: 'DELETE' })
  },
  dataverseEnvironments(): Promise<DataverseEnvironmentsResponse> {
    return apiFetch(API_ENDPOINTS.dataverseEnvironments())
  },
}

// =============================================================================
// Approval templates API (app_admin)
// =============================================================================

/** Tenant-wide API keys (app_admin): one key for every template of the company. */
export const apiKeysApi = {
  list(): Promise<{ keys: TenantApiKeySummary[] }> {
    return apiFetch(API_ENDPOINTS.tenantApiKeys())
  },
  create(name: string): Promise<CreateTenantApiKeyResponse> {
    return apiFetch(API_ENDPOINTS.tenantApiKeys(), { method: 'POST', body: JSON.stringify({ name }) })
  },
  revoke(id: string): Promise<{ success: boolean }> {
    return apiFetch(API_ENDPOINTS.tenantApiKey(id), { method: 'DELETE' })
  },
}

/**
 * A non-Lark account's own templates — `api` templates its systems fill through the
 * document API, and the Excel and Google Sheets templates its add-ins made — over the
 * authoring routes the Word add-in uses (40 §6.4). Any member may create and upload; a
 * delete is the tenant route (app_admin), as for every other template.
 */
export const authoringApi = {
  list(): Promise<AuthoringTemplateSummary[]> {
    return apiFetch(API_ENDPOINTS.authoringTemplates())
  },
  createApiTemplate(name: string): Promise<SourceTemplate> {
    return apiFetch(API_ENDPOINTS.authoringTemplates(), { method: 'POST', body: JSON.stringify({ name, sourceType: 'api' }) })
  },
  upload(id: string, file: File): Promise<SourceTemplateFileResponse> {
    const form = new FormData()
    form.append('file', file)
    return apiFetch(API_ENDPOINTS.authoringTemplateUpload(id), { method: 'POST', body: form })
  },
  download(id: string): Promise<{ blob: Blob; filename: string }> {
    return download(API_ENDPOINTS.authoringTemplateDownload(id), 'template.docx')
  },
  /** One template's settings for its page; the key and webhook only for whoever manages it. */
  get(id: string): Promise<AuthoringTemplateDetail> {
    return apiFetch(API_ENDPOINTS.authoringTemplate(id))
  },
  /** Only what changed: a write-back or webhook sent unchanged by a colleague is still refused when it differs. */
  update(id: string, body: UpdateTemplateRequest & { source?: unknown }): Promise<SourceTemplate> {
    return apiFetch(API_ENDPOINTS.authoringTemplate(id), { method: 'PUT', body: JSON.stringify(body) })
  },
  /** "Generate from fields": a starter laid out for the template's record mode, stored as its file. */
  starter(id: string, options: AutoGenerateTemplateRequest['options']): Promise<SourceTemplateFileResponse> {
    return apiFetch(API_ENDPOINTS.authoringTemplateStarter(id), { method: 'POST', body: JSON.stringify({ options }) })
  },
  /**
   * Become the owner of a source template, so runs nobody attends read with your own
   * connection. The source route, so it names the template's source as an add-in does.
   */
  claim(id: string, sourceKey: string): Promise<SourceTemplate> {
    return apiFetch(API_ENDPOINTS.sourceTemplateClaim(id), { method: 'PATCH', headers: { 'X-Source-Key': sourceKey } })
  },
  /** The columns its key, status and save settings can name, by the names the source takes. */
  columns(id: string): Promise<SourceColumnsResponse> {
    return apiFetch(API_ENDPOINTS.authoringTemplateColumns(id))
  },
  sharePointSites(q: string): Promise<SharePointSitesResponse> {
    return apiFetch(API_ENDPOINTS.authoringSharePointSites(q))
  },
  sharePointLists(siteId: string): Promise<SharePointListsResponse> {
    return apiFetch(API_ENDPOINTS.authoringSharePointLists(siteId))
  },
  dataverseTables(environmentUrl: string): Promise<DataverseTablesResponse> {
    return apiFetch(API_ENDPOINTS.authoringDataverseTables(environmentUrl))
  },
  /** A new SharePoint list or Dataverse template, bound after a read with the caller's own connection. */
  createSourceTemplate(name: string, view: Extract<ClientSourceView, { type: 'sharepoint_list' | 'dataverse' }>): Promise<SourceTemplate> {
    return apiFetch(API_ENDPOINTS.sourceTemplates(), { method: 'POST', body: JSON.stringify({ name, view }) })
  },
}

/**
 * The Generate page: documents from an account template filled in a form or from a file, as
 * the person signed in. Their status comes over the job's socket, as the extension's does.
 */
export const generateApi = {
  info(id: string): Promise<PortalGenerateInfo> {
    return apiFetch(API_ENDPOINTS.authoringTemplateGenerate(id))
  },
  generate(id: string, data: Record<string, unknown>[]): Promise<PortalGenerateResponse> {
    return apiFetch(API_ENDPOINTS.authoringTemplateGenerate(id), { method: 'POST', body: JSON.stringify({ data } satisfies PortalGenerateRequest) })
  },
  /**
   * Follow a job until it ends; rejects when its socket cannot be kept. The socket client is
   * loaded with the first job, not with the portal.
   */
  async watch(response: PortalGenerateResponse, onStatus: (status: WSJobStatusResponse) => void): Promise<unknown> {
    const { connectToJob } = await import('@lark-automadoc/shared-ui/client')
    return connectToJob(API_BASE, response, { onJobStatus: onStatus }).promise
  },
  /** A finished document's bytes, from its signed link (no session needed). */
  async file(downloadUrl: string): Promise<Uint8Array> {
    const response = await fetch(downloadUrl)
    if (!response.ok) throw Object.assign(new Error(`Download failed: ${response.status}`), { status: response.status })
    return new Uint8Array(await response.arrayBuffer())
  },
}

export const approvalsApi = {
  list(): Promise<GetApprovalTemplatesResponse> {
    return apiFetch(API_ENDPOINTS.approvalTemplates())
  },
  create(body: CreateApprovalTemplateRequest): Promise<ApprovalTemplateResponse> {
    return apiFetch(API_ENDPOINTS.approvalTemplates(), { method: 'POST', body: JSON.stringify(body) })
  },
  get(id: string): Promise<GetApprovalTemplateResponse> {
    return apiFetch(API_ENDPOINTS.approvalTemplate(id))
  },
  update(id: string, body: UpdateApprovalTemplateRequest): Promise<ApprovalTemplateResponse> {
    return apiFetch(API_ENDPOINTS.approvalTemplate(id), { method: 'PUT', body: JSON.stringify(body) })
  },
  upload(id: string, file: File): Promise<ApprovalTemplateResponse> {
    const form = new FormData()
    form.append('file', file)
    return apiFetch(API_ENDPOINTS.approvalTemplateUpload(id), { method: 'POST', body: form })
  },
  starter(id: string): Promise<ApprovalTemplateResponse> {
    return apiFetch(API_ENDPOINTS.approvalTemplateStarter(id), { method: 'POST' })
  },
  download(id: string): Promise<{ blob: Blob; filename: string }> {
    return apiRequest(API_ENDPOINTS.approvalTemplateDownload(id), {}, async (response) => {
      const disposition = response.headers.get('Content-Disposition') || ''
      const match = disposition.match(/filename\*=UTF-8''([^;]+)/) || disposition.match(/filename="([^"]+)"/)
      return { blob: await response.blob(), filename: match ? decodeURIComponent(match[1]) : 'template.docx' }
    })
  },
  /** The template as an `.automadoc` file, to import into another approval or company. */
  exportFile(id: string): Promise<{ blob: Blob; filename: string }> {
    return apiRequest(API_ENDPOINTS.approvalTemplateExport(id), {}, async (response) => {
      const disposition = response.headers.get('Content-Disposition') || ''
      const match = disposition.match(/filename\*=UTF-8''([^;]+)/) || disposition.match(/filename="([^"]+)"/)
      return { blob: await response.blob(), filename: match ? decodeURIComponent(match[1]) : 'template.automadoc' }
    })
  },
  /** An `.automadoc` file as a new template on the approval the link names. Starts as a Draft. */
  importFile(file: File, link: string, locale: string, timezone: string): Promise<ApprovalTemplateResponse> {
    const form = new FormData()
    form.append('file', file)
    form.append('link', link)
    form.append('locale', locale)
    form.append('timezone', timezone)
    return apiFetch(API_ENDPOINTS.approvalTemplateImport(), { method: 'POST', body: form })
  },
  requests(id: string): Promise<GetApprovalRequestsResponse> {
    return apiFetch(API_ENDPOINTS.approvalRequests(id))
  },
  /** The template's history; without options the newest twenty, as the test panel reads it. */
  runs(id: string, options: { page?: number; limit?: number; q?: string; status?: string; trigger?: string } = {}): Promise<GetApprovalRunsResponse> {
    const query = listParams(options)
    return apiFetch(`${API_ENDPOINTS.approvalRuns(id)}${query ? `?${query}` : ''}`)
  },
  startRun(id: string, instanceCode: string, deliver: boolean): Promise<StartApprovalRunResponse> {
    return apiFetch(API_ENDPOINTS.approvalRuns(id), { method: 'POST', body: JSON.stringify({ instanceCode, deliver }) })
  },
  chats(): Promise<GetApprovalChatsResponse> {
    return apiFetch(API_ENDPOINTS.approvalChats())
  },
  /** "I have checked the template": the current file fits the form as it is now. */
  formChecked(id: string): Promise<GetApprovalTemplateResponse> {
    return apiFetch(API_ENDPOINTS.approvalFormChecked(id), { method: 'POST' })
  },
  signatures(): Promise<GetApproverSignaturesResponse> {
    return apiFetch(API_ENDPOINTS.approverSignatures())
  },
  uploadSignature(openId: string, file: File): Promise<ApproverSignatureResponse> {
    const form = new FormData()
    form.append('file', file)
    return apiFetch(API_ENDPOINTS.approverSignature(openId), { method: 'PUT', body: form })
  },
  removeSignature(openId: string): Promise<{ success: boolean }> {
    return apiFetch(API_ENDPOINTS.approverSignature(openId), { method: 'DELETE' })
  },
}

// =============================================================================
// Signature requests across the tenant (app_admin)
// =============================================================================

/** A file download through the authenticated wrapper: the blob and the server's filename. */
function download(url: string, fallback: string): Promise<{ blob: Blob; filename: string }> {
  return apiRequest(url, {}, async (response) => {
    const disposition = response.headers.get('Content-Disposition') || ''
    const match = disposition.match(/filename\*=UTF-8''([^;]+)/) || disposition.match(/filename="([^"]+)"/)
    return { blob: await response.blob(), filename: match ? decodeURIComponent(match[1]) : fallback }
  })
}

export const signaturesApi = {
  list(options: { page?: number; limit?: number; q?: string; status?: string } = {}): Promise<ListSignatureRequestsResponse> {
    const query = listParams(options)
    return apiFetch(`${API_ENDPOINTS.tenantSignatures()}${query ? `?${query}` : ''}`)
  },
  get(id: string): Promise<GetSignatureRequestResponse> {
    return apiFetch(API_ENDPOINTS.signatureRequest(id, 'tenant'))
  },
  cancel(id: string): Promise<{ success: boolean }> {
    return apiFetch(`${API_ENDPOINTS.signatureRequest(id, 'tenant')}/cancel`, { method: 'POST' })
  },
  remind(id: string, signerId: string): Promise<RemindSignerResponse> {
    return apiFetch(`${API_ENDPOINTS.signatureRequest(id, 'tenant')}/signers/${signerId}/remind`, { method: 'POST' })
  },
  document(id: string): Promise<{ blob: Blob; filename: string }> {
    return download(`${API_ENDPOINTS.signatureRequest(id, 'tenant')}/document`, 'document.pdf')
  },
  audit(id: string): Promise<{ blob: Blob; filename: string }> {
    return download(`${API_ENDPOINTS.signatureRequest(id, 'tenant')}/audit`, 'audit.csv')
  },
}

// =============================================================================
// AutomaDoc Sign (/api/esign/*, every platform; contract 91 §2)
// =============================================================================

const esignUrl = (route: Parameters<typeof esignPath>[0], params: Record<string, string> = {}, query = ''): string =>
  `${API_BASE}${esignPath(route, params)}${query ? `?${query}` : ''}`

const jsonBody = (body: unknown): RequestInit => ({ body: JSON.stringify(body) })

export const esignApi = {
  list(query: ListEnvelopesQuery): Promise<ListEnvelopesResponse> {
    return apiFetch(esignUrl('listEnvelopes', {}, listParams(query as Record<string, string | number | undefined>)))
  },
  create(body: CreateEnvelopeBody): Promise<EnvelopeResponse> {
    return apiFetch(esignUrl('createEnvelope'), { method: 'POST', ...jsonBody(body) })
  },
  get(envelopeId: string): Promise<EnvelopeResponse> {
    return apiFetch(esignUrl('getEnvelope', { envelopeId }))
  },
  update(envelopeId: string, body: UpdateEnvelopeBody): Promise<EnvelopeResponse> {
    return apiFetch(esignUrl('updateEnvelope', { envelopeId }), { method: 'PATCH', ...jsonBody(body) })
  },
  deleteDraft(envelopeId: string): Promise<{ success: boolean }> {
    return apiFetch(esignUrl('deleteDraft', { envelopeId }), { method: 'DELETE' })
  },
  upload(envelopeId: string, file: File): Promise<DocumentResponse> {
    const form = new FormData()
    form.set('file', file, file.name)
    form.set('name', file.name.replace(/\.(pdf|docx|xlsx|pptx)$/i, ''))
    return apiFetch(esignUrl('uploadDocument', { envelopeId }), { method: 'POST', body: form })
  },
  addGenerated(envelopeId: string, body: AddGeneratedDocumentBody): Promise<DocumentResponse> {
    return apiFetch(esignUrl('addGeneratedDocument', { envelopeId }), { method: 'POST', ...jsonBody(body) })
  },
  removeDocument(envelopeId: string, documentId: string): Promise<{ success: boolean }> {
    return apiFetch(esignUrl('removeDocument', { envelopeId, documentId }), { method: 'DELETE' })
  },
  pages(envelopeId: string, documentId: string): Promise<DocumentPagesResponse> {
    return apiFetch(esignUrl('documentPages', { envelopeId, documentId }))
  },
  putRecipients(envelopeId: string, recipients: EnvelopeRecipientInput[]): Promise<RecipientsResponse> {
    return apiFetch(esignUrl('putRecipients', { envelopeId }), { method: 'PUT', ...jsonBody({ recipients }) })
  },
  putFields(envelopeId: string, fields: EnvelopeFieldInput[]): Promise<FieldsResponse> {
    return apiFetch(esignUrl('putFields', { envelopeId }), { method: 'PUT', ...jsonBody({ fields }) })
  },
  applyTags(envelopeId: string, slots: Record<string, string>): Promise<ApplyTagsResponse> {
    return apiFetch(esignUrl('applyTags', { envelopeId }), { method: 'POST', ...jsonBody({ slots }) })
  },
  send(envelopeId: string): Promise<SendEnvelopeResponse> {
    return apiFetch(esignUrl('sendEnvelope', { envelopeId }), { method: 'POST' })
  },
  /** Multipart: `payload` and, unless adopted from the saved ones, the pictures. */
  selfSign(envelopeId: string, payload: SelfSignPayload, pictures: { signature?: Blob; initials?: Blob }): Promise<EnvelopeResponse> {
    const form = new FormData()
    form.set('payload', JSON.stringify(payload))
    if (pictures.signature) form.set('signature', pictures.signature, 'signature.png')
    if (pictures.initials) form.set('initials', pictures.initials, 'initials.png')
    return apiFetch(esignUrl('selfSign', { envelopeId }), { method: 'POST', body: form })
  },
  void(envelopeId: string, reason: string): Promise<EnvelopeResponse> {
    return apiFetch(esignUrl('voidEnvelope', { envelopeId }), { method: 'POST', ...jsonBody({ reason }) })
  },
  remind(envelopeId: string, recipientId: string): Promise<RemindRecipientResponse> {
    return apiFetch(esignUrl('remindRecipient', { envelopeId, recipientId }), { method: 'POST' })
  },
  resend(envelopeId: string, recipientId: string): Promise<ResendLinkResponse> {
    return apiFetch(esignUrl('resendLink', { envelopeId, recipientId }), { method: 'POST' })
  },
  events(envelopeId: string): Promise<EnvelopeEventsResponse> {
    return apiFetch(esignUrl('envelopeEvents', { envelopeId }))
  },
  /** A short-lived signed link to the file (an attachment download). */
  download(envelopeId: string, file: EsignDownloadFile, documentId?: string): Promise<EsignDownload> {
    return apiFetch(esignUrl('downloadFile', { envelopeId }, listParams({ file, documentId })))
  },
  deleteFiles(envelopeId: string): Promise<EnvelopeResponse> {
    return apiFetch(esignUrl('deleteFiles', { envelopeId }), { method: 'POST' })
  },
  saveLayout(envelopeId: string, name: string): Promise<LayoutResponse> {
    return apiFetch(esignUrl('saveAsLayout', { envelopeId }), { method: 'POST', ...jsonBody({ name }) })
  },
  layouts(): Promise<ListLayoutsResponse> {
    return apiFetch(esignUrl('listLayouts'))
  },
  updateLayout(layoutId: string, body: UpdateLayoutBody): Promise<LayoutResponse> {
    return apiFetch(esignUrl('updateLayout', { layoutId }), { method: 'PATCH', ...jsonBody(body) })
  },
  deleteLayout(layoutId: string): Promise<{ success: boolean }> {
    return apiFetch(esignUrl('deleteLayout', { layoutId }), { method: 'DELETE' })
  },
  savedSignatures(): Promise<SavedSignaturesResponse> {
    return apiFetch(esignUrl('getSavedSignatures'))
  },
  /** The PNG is the raw body. */
  putSavedSignature(kind: EsignSavedSignatureKind, png: Blob): Promise<SavedSignaturesResponse> {
    return apiFetch(esignUrl('putSavedSignature', { kind }), { method: 'PUT', body: png, headers: { 'Content-Type': 'image/png' } })
  },
  deleteSavedSignature(kind: EsignSavedSignatureKind): Promise<SavedSignaturesResponse> {
    return apiFetch(esignUrl('deleteSavedSignature', { kind }), { method: 'DELETE' })
  },
}

// =============================================================================
// Template designer API
// =============================================================================

/**
 * Which template the designer has open. A Base template is reached through the
 * Base routes, which take the Base and table as headers — the extension passes
 * them in the link; an approval template through the app_admin routes; an
 * account's source or api template through the authoring routes.
 */
export type DesignLocation =
  | { source: 'base'; id: string; baseToken: string; tableId: string }
  | { source: 'approval'; id: string }
  | { source: 'account'; id: string }

function designHeaders(location: DesignLocation): Record<string, string> {
  return location.source === 'base'
    ? { 'X-Lark-Base-Token': location.baseToken, 'X-Lark-Table-Id': location.tableId }
    : {}
}

function designUrl(location: DesignLocation, publish = false): string {
  if (location.source === 'base') {
    return publish ? API_ENDPOINTS.templateDesignPublish(location.id) : API_ENDPOINTS.templateDesign(location.id)
  }
  if (location.source === 'account') {
    return publish ? API_ENDPOINTS.authoringTemplateDesignPublish(location.id) : API_ENDPOINTS.authoringTemplateDesign(location.id)
  }
  return publish ? API_ENDPOINTS.approvalDesignPublish(location.id) : API_ENDPOINTS.approvalDesign(location.id)
}

export const designApi = {
  get(location: DesignLocation): Promise<GetTemplateDesignResponse> {
    return apiFetch(designUrl(location), { headers: designHeaders(location) })
  },
  save(location: DesignLocation, body: SaveTemplateDesignRequest): Promise<SaveTemplateDesignResponse> {
    return apiFetch(designUrl(location), { method: 'PUT', headers: designHeaders(location), body: JSON.stringify(body) })
  },
  publish(location: DesignLocation, body: PublishTemplateDesignRequest): Promise<PublishTemplateDesignResponse> {
    return apiFetch(designUrl(location, true), { method: 'POST', headers: designHeaders(location), body: JSON.stringify(body) })
  },
  /** The current file, for finishing in Word. */
  download(location: DesignLocation): Promise<{ blob: Blob; filename: string }> {
    if (location.source === 'approval') return approvalsApi.download(location.id)
    if (location.source === 'account') return authoringApi.download(location.id)
    return apiRequest(API_ENDPOINTS.downloadTemplate(location.id), { headers: designHeaders(location) }, async (response) => {
      const disposition = response.headers.get('Content-Disposition') || ''
      const match = disposition.match(/filename\*=UTF-8''([^;]+)/) || disposition.match(/filename="([^"]+)"/)
      return { blob: await response.blob(), filename: match ? decodeURIComponent(match[1]) : 'template.docx' }
    })
  },
  /** One test document from a Base record — the extension's generation, run as a preview. */
  generateBase(location: Extract<DesignLocation, { source: 'base' }>, recordId: string): Promise<GenerateResponse> {
    return apiFetch(API_ENDPOINTS.generate(location.id), {
      method: 'POST',
      headers: designHeaders(location),
      // A test: nothing written back, no webhook, free up to the day's allowance.
      body: JSON.stringify({ recordIds: [recordId], preview: true } satisfies GenerateRequest),
    })
  },
  /** Save an account template's sample data; an api template's fields come from it. */
  saveSample(location: Extract<DesignLocation, { source: 'account' }>, sample: unknown): Promise<{ source: DesignSource }> {
    return apiFetch(API_ENDPOINTS.authoringTemplateDesignSample(location.id), {
      method: 'PUT',
      body: JSON.stringify({ sample } satisfies PutDesignSampleRequest),
    })
  },
  /** One test document of an account template: from `sample`, or without it from the source's first record. */
  previewAccount(location: Extract<DesignLocation, { source: 'account' }>, sample?: unknown): Promise<GenerateResponse> {
    return apiFetch(API_ENDPOINTS.authoringTemplateDesignPreview(location.id), {
      method: 'POST',
      body: JSON.stringify((sample === undefined ? {} : { sample }) satisfies PreviewDesignRequest),
    })
  },
}

// =============================================================================
// Support API
// =============================================================================

export const supportApi = {
  async sendMessage(
    message: string,
    requestType: SupportRequest['requestType'] = 'general',
    locale?: string
  ): Promise<SupportRequestResponse> {
    const body: SupportRequest = { requestType, message, locale }
    return apiFetch<SupportRequestResponse>(API_ENDPOINTS.supportRequest(), {
      method: 'POST',
      body: JSON.stringify(body),
    })
  },
}

/**
 * The signed-in person's contact preferences (Notifications page), and their portal
 * language, recorded so emails reach them in it (contact center).
 */
export const contactPreferencesApi = {
  get(): Promise<ContactPreferencesResponse> {
    return apiFetch<ContactPreferencesResponse>(API_ENDPOINTS.contactPreferences())
  },
  update(body: UpdateContactPreferencesRequest): Promise<ContactPreferencesResponse> {
    return apiFetch<ContactPreferencesResponse>(API_ENDPOINTS.contactPreferences(), { method: 'PUT', body: JSON.stringify(body) })
  },
  /** Once per language per browser session; a failure is only a missed hint. */
  async reportLocale(locale: string): Promise<void> {
    const key = 'automadoc-contact-locale'
    try {
      if (sessionStorage.getItem(key) === locale) return
    } catch {
      // Storage blocked: report every time, it is one small request.
    }
    try {
      await apiFetch(API_ENDPOINTS.contactPreferences(), { method: 'PUT', body: JSON.stringify({ locale }) })
      try {
        sessionStorage.setItem(key, locale)
      } catch {
        // Nothing to remember it in.
      }
    } catch {
      // Not worth telling anyone.
    }
  },
}
